// ==================== SUB-ACCOUNT FORM COMPONENT ====================
// Create / edit name / set password for a sub-account.
// variant 'modal': body + footer laid out for a Modal (customer page).
// variant 'panel': inline panel inside another modal (admin), like AddMembersPanel.

import { useState, type FormEvent, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { User, Mail, Phone, Lock, Loader2, AlertCircle, X, ChevronLeft } from 'lucide-react';
import { Button, Input, Select } from '../ui';
import {
  subAccountCreateSchema,
  subAccountEditSchema,
  subAccountPasswordSchema,
  type SubAccountCreateData,
  type SubAccountEditData,
  type SubAccountPasswordData,
} from '../../utils/form-validations';
import type { SubAccount, SubAccountsApi } from '../../services/sub-accounts.service';

// ==================== TYPES ====================

export type SubAccountFormMode = 'create' | 'edit' | 'password';

interface SubAccountFormProps {
  mode: SubAccountFormMode;
  accounts?: Array<{ id: string; businessName: string }>;
  defaultOrganizationId?: string;
  subAccount?: SubAccount | null;
  api: SubAccountsApi;
  onCancel: () => void;
  onSaved: (message: string) => void;
  variant?: 'modal' | 'panel';
}

// ==================== FORM LAYOUT COMPONENT ====================

interface FormLayoutProps {
  variant: 'modal' | 'panel';
  title: string;
  subtitle: string;
  error: string | null;
  onDismissError: () => void;
  onCancel: () => void;
  onSubmit: (e?: FormEvent) => void;
  saving: boolean;
  isValid: boolean;
  submitLabel: string;
  savingLabel: string;
  children: ReactNode;
}

const FormLayout = ({
  variant,
  title,
  subtitle,
  error,
  onDismissError,
  onCancel,
  onSubmit,
  saving,
  isValid,
  submitLabel,
  savingLabel,
  children,
}: FormLayoutProps) => {
  const actions = (
    <>
      <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
        Cancel
      </Button>
      <Button type="submit" disabled={!isValid || saving}>
        {saving ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {savingLabel}
          </>
        ) : (
          <>{submitLabel}</>
        )}
      </Button>
    </>
  );

  if (variant === 'panel') {
    return (
      <form onSubmit={onSubmit} className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-(--color-muted-gray)" />
            </button>
            <div>
              <h3 className="text-sm font-medium text-white">{title}</h3>
              <p className="text-xs text-(--color-muted-gray)">{subtitle}</p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <p className="text-sm text-red-300">{error}</p>
            <button type="button" onClick={onDismissError} className="ml-auto">
              <X className="w-4 h-4 text-red-400" />
            </button>
          </div>
        )}

        <div className="space-y-5">{children}</div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
          {actions}
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={onSubmit}>
      <div className="space-y-5 p-6">{children}</div>

      {/* Modal Error */}
      {error && (
        <div className="mx-6 mb-4 flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
          <p className="text-sm text-red-300 flex-1">{error}</p>
          <button type="button" onClick={onDismissError} className="text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-(--color-border-gray)">
        {actions}
      </div>
    </form>
  );
};

// ==================== PASSWORD REQUIREMENTS COMPONENT ====================

const PasswordRequirements = () => (
  <div className="p-4 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
    <h3 className="text-sm font-medium text-white mb-2">Password Requirements</h3>
    <ul className="text-sm text-(--color-muted-gray) space-y-1">
      <li>• At least 8 characters long</li>
      <li>• Contains at least one uppercase letter</li>
      <li>• Contains at least one lowercase letter</li>
      <li>• Contains at least one number</li>
      <li>• Contains at least one special character (@$!%*?&)</li>
    </ul>
  </div>
);

// ==================== CREATE FORM ====================

const CreateSubAccountForm = ({ api, onCancel, onSaved, variant = 'modal', accounts, defaultOrganizationId }: SubAccountFormProps) => {
  const [organizationId, setOrganizationId] = useState(defaultOrganizationId ?? accounts?.[0]?.id);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<SubAccountCreateData>({
    resolver: zodResolver(subAccountCreateSchema),
    mode: 'onChange',
  });

  const onSubmit = async (data: SubAccountCreateData) => {
    try {
      setSaving(true);
      setModalError(null);
      await api.create({
        ...(accounts && organizationId && { organizationId }),
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
        personalEmail: data.personalEmail.trim(),
        phoneNumber: data.phoneNumber.trim(),
        password: data.password,
      });
      onSaved('Sub-account created successfully');
    } catch (err: unknown) {
      console.error('Failed to create sub-account:', err);
      setModalError(err instanceof Error ? err.message : 'Failed to create sub-account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormLayout
      variant={variant}
      title="Add Sub-account"
      subtitle="Give a team member access to assigned contacts and WhatsApp chat"
      error={modalError}
      onDismissError={() => setModalError(null)}
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
      saving={saving}
      isValid={isValid}
      submitLabel="Create Sub-account"
      savingLabel="Creating..."
    >
      {accounts && accounts.length > 1 && (
        <Select
          label="Account"
          options={accounts.map(account => ({ value: account.id, label: account.businessName }))}
          value={organizationId || ''}
          onChange={value => setOrganizationId(value as string)}
        />
      )}
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="First Name"
          placeholder="e.g., Ali"
          leftIcon={<User className="w-4 h-4" />}
          {...register('firstName')}
          error={errors.firstName?.message}
        />
        <Input
          label="Last Name"
          placeholder="e.g., Khan"
          leftIcon={<User className="w-4 h-4" />}
          {...register('lastName')}
          error={errors.lastName?.message}
        />
      </div>

      <Input
        label="Email Address"
        type="email"
        placeholder="e.g., agent@company.com"
        leftIcon={<Mail className="w-4 h-4" />}
        hint="The sub-account signs in with this email"
        {...register('email')}
        error={errors.email?.message}
      />

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Personal Email"
          type="email"
          placeholder="e.g., ali.khan@gmail.com"
          leftIcon={<Mail className="w-4 h-4" />}
          {...register('personalEmail')}
          error={errors.personalEmail?.message}
        />
        <Input
          label="Contact Number"
          type="tel"
          placeholder="+1 (555) 123-4567"
          leftIcon={<Phone className="w-4 h-4" />}
          {...register('phoneNumber')}
          error={errors.phoneNumber?.message}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Password"
          type="password"
          placeholder="Enter password"
          leftIcon={<Lock className="w-4 h-4" />}
          showPasswordToggle
          {...register('password')}
          error={errors.password?.message}
        />
        <Input
          label="Confirm Password"
          type="password"
          placeholder="Confirm password"
          leftIcon={<Lock className="w-4 h-4" />}
          showPasswordToggle
          {...register('confirmPassword')}
          error={errors.confirmPassword?.message}
        />
      </div>

      <PasswordRequirements />
    </FormLayout>
  );
};

// ==================== EDIT FORM ====================

const EditSubAccountForm = ({ subAccount, api, onCancel, onSaved, variant = 'modal', accounts, defaultOrganizationId }: SubAccountFormProps) => {
  const [organizationId, setOrganizationId] = useState(subAccount?.assignedOrganizationId ?? defaultOrganizationId ?? accounts?.[0]?.id);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<SubAccountEditData>({
    resolver: zodResolver(subAccountEditSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: subAccount?.firstName || '',
      lastName: subAccount?.lastName || '',
      personalEmail: subAccount?.personalEmail || '',
      phoneNumber: subAccount?.phoneNumber || '',
    },
  });

  const onSubmit = async (data: SubAccountEditData) => {
    if (!subAccount) return;

    try {
      setSaving(true);
      setModalError(null);
      await api.update(subAccount.id, {
        ...(accounts && organizationId && { organizationId }),
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        personalEmail: data.personalEmail.trim(),
        phoneNumber: data.phoneNumber.trim(),
      });
      onSaved('Sub-account updated successfully');
    } catch (err: unknown) {
      console.error('Failed to update sub-account:', err);
      setModalError(err instanceof Error ? err.message : 'Failed to update sub-account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormLayout
      variant={variant}
      title="Edit Sub-account"
      subtitle={subAccount?.email || ''}
      error={modalError}
      onDismissError={() => setModalError(null)}
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
      saving={saving}
      isValid={isValid}
      submitLabel="Update Sub-account"
      savingLabel="Saving..."
    >
      {accounts && accounts.length > 1 && (
        <Select
          label="Account"
          options={accounts.map(account => ({ value: account.id, label: account.businessName }))}
          value={organizationId || ''}
          onChange={value => setOrganizationId(value as string)}
        />
      )}
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="First Name"
          placeholder="e.g., Ali"
          leftIcon={<User className="w-4 h-4" />}
          {...register('firstName')}
          error={errors.firstName?.message}
        />
        <Input
          label="Last Name"
          placeholder="e.g., Khan"
          leftIcon={<User className="w-4 h-4" />}
          {...register('lastName')}
          error={errors.lastName?.message}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-(--color-soft-gray) mb-2">
          Email Address
        </label>
        <div className="relative">
          <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-(--color-muted-gray)" />
          <input
            type="email"
            value={subAccount?.email || ''}
            disabled
            className="w-full pl-10 pr-4 py-3 bg-(--color-navy-light)/50 border border-(--color-border-gray) rounded-md text-(--color-muted-gray) cursor-not-allowed"
          />
        </div>
        <p className="text-xs text-(--color-muted-gray) mt-1">
          Email cannot be changed. Delete and re-create the sub-account to use another email.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Personal Email"
          type="email"
          placeholder="e.g., ali.khan@gmail.com"
          leftIcon={<Mail className="w-4 h-4" />}
          {...register('personalEmail')}
          error={errors.personalEmail?.message}
        />
        <Input
          label="Contact Number"
          type="tel"
          placeholder="+1 (555) 123-4567"
          leftIcon={<Phone className="w-4 h-4" />}
          {...register('phoneNumber')}
          error={errors.phoneNumber?.message}
        />
      </div>
    </FormLayout>
  );
};

// ==================== SET PASSWORD FORM ====================

const SetPasswordForm = ({ subAccount, api, onCancel, onSaved, variant = 'modal' }: SubAccountFormProps) => {
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<SubAccountPasswordData>({
    resolver: zodResolver(subAccountPasswordSchema),
    mode: 'onChange',
  });

  const onSubmit = async (data: SubAccountPasswordData) => {
    if (!subAccount) return;

    try {
      setSaving(true);
      setModalError(null);
      await api.resetPassword(subAccount.id, data.password);
      onSaved('Password updated successfully. The sub-account has been signed out everywhere.');
    } catch (err: unknown) {
      console.error('Failed to update sub-account password:', err);
      setModalError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormLayout
      variant={variant}
      title="Set New Password"
      subtitle={subAccount?.email || ''}
      error={modalError}
      onDismissError={() => setModalError(null)}
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
      saving={saving}
      isValid={isValid}
      submitLabel="Update Password"
      savingLabel="Updating..."
    >
      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="New Password"
          type="password"
          placeholder="Enter new password"
          leftIcon={<Lock className="w-4 h-4" />}
          showPasswordToggle
          {...register('password')}
          error={errors.password?.message}
        />
        <Input
          label="Confirm New Password"
          type="password"
          placeholder="Confirm new password"
          leftIcon={<Lock className="w-4 h-4" />}
          showPasswordToggle
          {...register('confirmPassword')}
          error={errors.confirmPassword?.message}
        />
      </div>

      <PasswordRequirements />
    </FormLayout>
  );
};

// ==================== SUB-ACCOUNT FORM ====================

export const SubAccountForm = (props: SubAccountFormProps) => {
  if (props.mode === 'edit') return <EditSubAccountForm {...props} />;
  if (props.mode === 'password') return <SetPasswordForm {...props} />;
  return <CreateSubAccountForm {...props} />;
};

export default SubAccountForm;
